import JWT from 'jsonwebtoken';
import { v4 as UUID } from 'uuid';
import Analytics from '../analytics';
import Environment from '../environment';
import HwpTvApi from '../hwpTvApi';
import { getLogger } from '../logging/logger';
import MoneyBadger from '../platform/comcastMoneyBadger';
import StorageManager from './persistentStorageManager';

const REGEX_UUID = /^\w{8}-\w{4}-\w{4}-\w{4}-\w{12}$/i;
const ID_STORAGE_NAME = 'hwptvId';

const logger = getLogger('AppAuthManager');

function intToHex(nr) {
	return nr.toString(16).padStart(2, '0');
}

function getRandomString(bytes = 30) {
	const randomValues = new Uint8Array(bytes);
	window.crypto.getRandomValues(randomValues);
	return Array.from(randomValues).map(intToHex).join('');
}

function getAuth() {
	logger.log('[getAuth]');

	return import('../hwptvAmplify').then((Auth) => Auth.default);
}

class AppAuthManager {
	initialize(options = {}) {
		logger.log('[initialize]');

		Object.assign(this, {
			accountVariant: Environment.isKidsVariant() ? 'kids' : undefined,
			...options,
		});

		this.isReady(); // Make sure ready promise is created

		const overrideId = this.findUserCredentials();

		HwpTvApi.off('error');
		HwpTvApi.on('error', this.onApiError.bind(this));

		if (overrideId) {
			logger.info(`[OVERRIDE ID: ${overrideId}]`);
			HwpTvApi.headers.Authorization = overrideId;

			const params = new URLSearchParams(window.location.search);
			const session = params.get('session');
			if (session) {
				HwpTvApi.headers.session = session;
			}

			return this.authorize().then(
				() => {
					logger.debug('[Override Initialization Complete]');
					this.onReadyResolve();
					return this.isReady();
				},
				(error) => {
					const msg = 'supplied id resulted in authorization error';
					logger.error(msg, { error });
					this.onReadyReject(error);
					return this.isReady();
				},
			);
		}

		if (Environment.isComcastSetTopBox()) {
			return this.loginStb()
				.catch(() => this.loginAnonymous())
				.then(() => this.checkHwptvId().catch((error) => logger.error({ error })))
				.then(
					() => {
						logger.info('[STB Initialization Complete]');
						this.onReadyResolve();
						return this.isReady();
					},
					(error) => {
						logger.error('[login STB error]', { error });
						this.onReadyReject();
						return this.isReady();
					},
				);
		}

		return this.loginAnonymous()
			.then(() => this.checkHwptvId().catch((error) => logger.error({ error })))
			.then(
				() => {
					logger.info('[Initialization Complete]');
					this.onReadyResolve();
					return this.isReady();
				},
				(error) => {
					logger.error('[login anonymous failure]', { error });
					this.onReadyReject();
					return this.isReady();
				},
			)
			.then(() => this.isReady());
	}

	/**
	 * Returns promise that is called when the AppAuthManager is ready to be used by other modules
	 */
	isReady() {
		logger.log('[isReady]');

		if (!this.readyPromise) {
			this.readyPromise = new Promise((resolve, reject) => {
				this.onReadyResolve = resolve;
				this.onReadyReject = reject;
			});
		}

		return this.readyPromise;
	}

	/**
	 * Retrieves or creates UUID for use in the HollywoodPlayer system, sets it in the
	 * Authorization header, and calls the authorize method
	 */
	loginAnonymous() {
		logger.log('[loginAnonymous]');

		let hwptvId = StorageManager.getCookie(ID_STORAGE_NAME);
		if (!hwptvId) {
			hwptvId = UUID();
			StorageManager.setCookie(ID_STORAGE_NAME, hwptvId);
		}
		HwpTvApi.headers.Authorization = hwptvId;

		if (!HwpTvApi.headers.platform) {
			HwpTvApi.headers.platform = Environment.isVerizonSetTopBox() ? 'verizon' : 'web';
		} else {
			// Already set during another login method before failing and falling back to this login method
		}

		return this.authorize();
	}

	loginStb() {
		logger.log('[loginStb]');

		return MoneyBadger.getMoneyBadgerService()
			.then((bridgeService) => {
				const isActive = MoneyBadger.isActive();

				if (bridgeService && isActive) {
					// We're ready!
					logger.info('[MoneyBadger Ready!]');
					return Promise.resolve();
				}

				if (bridgeService && !isActive) {
					// wait for service to be ready
					logger.info('[Wait for MoneyBadger to be ready...]');
					return new Promise((resolve) => MoneyBadger.on('ready', resolve));
				}

				// No service available
				logger.error('[MoneyBadger Bridge Not Available]');
				Analytics.sendEvent('AppAuthManager-Error', {
					error: 'MoneyBadger bridge not available',
				});
				return Promise.reject(new Error('Bridge not available'));
			})
			.then(() =>
				MoneyBadger.getUserInfo().then(
					(response) => {
						HwpTvApi.headers.device = response.deviceHash;
						HwpTvApi.headers.platform = response.partnerId;

						if (!response.householdId) {
							const error = new Error('MoneyBadger did not provide "householdId"');
							logger.warn(error.message);
							return Promise.reject(error);
						}
						HwpTvApi.headers.Authorization = response.householdId;

						return this.authorize().then((token) => {
							try {
								// Capture HWP ID from token in case Comcast removes the householdId from us in the future
								const payload = JWT.decode(token);
								StorageManager.setCookie(ID_STORAGE_NAME, payload.id);
							} catch (e) {
								logger.warn('[Identifier not a token!]');
							}
						});
					},
					(error) => {
						Analytics.sendEvent('AppAuthManager-Error', {
							status: 'MoneyBadger.getUserInfo Error',
							error: JSON.stringify({ error }),
						});
						logger.error('[Failed to get MoneyBadger user info]', error);
					},
				),
			);
	}

	/**
	 * Retrieve or capture a hwptvId for the current Cognito user
	 */
	checkHwptvId() {
		logger.log('[checkHwptvId]');

		return getAuth()
			.then((Auth) => Auth.currentAuthenticatedUser())
			.then((user) => {
				if (!user) {
					return Promise.resolve();
				}

				let localHwptvId = this.getIdentifier();
				// Try to decode token and get id
				try {
					const payload = JWT.decode(localHwptvId);
					localHwptvId = payload.id;
				} catch (e) {
					logger.warn('[Identifier not a token!]');
				}

				const userAttributes = user.attributes;
				const cognitoHwptvId = user.attributes['custom:hwptvId'];
				if (cognitoHwptvId && cognitoHwptvId !== localHwptvId) {
					// Copy cognito hwptvId to local hwptvId and re-authorize
					StorageManager.setCookie(ID_STORAGE_NAME, cognitoHwptvId);
					HwpTvApi.headers.Authorization = cognitoHwptvId;
					// Reauthorize with API after updating identifier
					logger.info({ cognitoHwptvId });
					return this.authorize();
				}

				if (!cognitoHwptvId && localHwptvId) {
					// No hwptvId set on Cognito user. Copy from local hwptvId
					Object.assign(userAttributes, { 'custom:hwptvId': localHwptvId });
					logger.info({ localHwptvId });
					return this.updateUserAttributes(userAttributes);
				}

				return Promise.resolve();
			});
	}

	/**
	 * Call to the HWP TV API to retrieve a JWT token use for easily identifying
	 * this user in the future. **Wrapped in promise to convert jquery Deferred to
	 * a native promise.
	 */
	authorize() {
		logger.log('[authorize]');

		// variant handled iff we are redirecting a user with their token
		if (HwpTvApi.headers.Authorization.search('Bearer') === -1) {
			// update our header due to new account types
			HwpTvApi.headers.Authorization = this.accountVariant
				? `${HwpTvApi.headers.Authorization}-${this.accountVariant}`
				: HwpTvApi.headers.Authorization;
		}

		return HwpTvApi.authorize().then(({ token }) => {
			if (token) {
				HwpTvApi.headers.Authorization = `Bearer ${token}`;
				const { id: hwptvId } = JWT.decode(token);
				Analytics.setUserId(hwptvId);
				return token;
			}

			const error = new Error('Missing Token!');
			logger.error(error);
			return Promise.reject(error);
		});
	}

	/**
	 * Signs up a new user to the Cognito User Pool
	 * @param {String} username
	 */
	signUp(username) {
		logger.log('[signUp]', { username });

		const password = getRandomString();
		return getAuth()
			.then((Auth) =>
				Auth.signUp({
					username,
					password,
					attributes: {
						'custom:hwptvId': this.getUserIdentifier(),
					},
				}),
			)
			.then((user) => {
				logger.debug({ user });
				this.user = user;
				return user;
			});
	}

	/**
	 * Signs an existing user into the Cognito User Pool
	 * @param {String} username Name to use when logging into Cognito User Pool. Currently only
	 * email is supported at this time of this documentation.
	 */
	signIn(username) {
		logger.log('[signIn]', { username });

		return getAuth()
			.then((Auth) => Auth.signIn(username))
			.then((user) => {
				logger.debug({ user });
				this.user = user;
				return user;
			});
	}

	/**
	 * Signs a user out of the Cogntio User Pool
	 */
	signOut() {
		logger.log('[signOut]');

		return getAuth()
			.then((Auth) => Auth.signOut())
			.then(
				() => {
					logger.debug('[Logged Out]');
					this.user = undefined;
					HwpTvApi.headers.Authorization = '';
					StorageManager.setCookie(ID_STORAGE_NAME, '');
					return this.initialize();
				},
				(logoutError) => logger.debug({ logoutError }),
			);
	}

	/**
	 * Submits a temporary password code that was sent to the user
	 * as part of the Custom Cognito Authentication system
	 * @param {String} code	The temporary code entered by the user
	 */
	submitCode(code) {
		logger.log('[submitCode]', { code });

		return getAuth()
			.then((Auth) => Auth.sendCustomChallengeAnswer(this.user, code))
			.then((cognitoUser) => {
				if (!cognitoUser.signInUserSession) {
					const error = new Error('Invalid Code');
					return Promise.reject(error);
				}

				return this.checkHwptvId().then(() => cognitoUser);
			});
	}

	/**
	 * Gets attributes for the currently logged in Cognito user, if available.
	 */
	getUserAttributes() {
		logger.log('[getUserAttributes]');

		return getAuth()
			.then((Auth) => Auth.currentUserInfo())
			.then((cognitoUser) => {
				if (cognitoUser) {
					return cognitoUser.attributes;
				}
				return undefined;
			});
	}

	/**
	 * Stores given attributes to the current authenticated Cognito User
	 * @param {Object} attributes data to be stored for user
	 */
	updateUserAttributes(attributes) {
		logger.log('[updateUserAttributes]', { attributes });

		return getAuth().then(
			(Auth) => {
				Auth.currentAuthenticatedUser().then(
					(cognitoUser) => {
						logger.debug({ cognitoUser });
						if (cognitoUser) {
							return Auth.updateUserAttributes(cognitoUser, attributes);
						}

						return Promise.reject(new Error('No user to update'));
					},
					(error) => {
						logger.error(error);
						return error;
					},
				);
			},
			(error) => {
				logger.error(error);
				return error;
			},
		);
	}

	/**
	 * Gets the current value (or token) from the Authorization header that is
	 * being sent too the HWP TV API.
	 */
	getIdentifier() {
		let identifier = HwpTvApi.headers.Authorization;
		if (identifier) {
			identifier = identifier.replace('Bearer ', '');
		}
		return identifier;
	}

	getUserIdentifier() {
		logger.log('[getUserIdentifier]');

		const identifier = this.getIdentifier();
		try {
			const payload = JWT.decode(identifier);
			return payload.id;
		} catch (error) {
			logger.error('[decode token error]', error);
		}

		return identifier;
	}

	/**
	 * Search for any user identifiers that have been supplied in the query parameters
	 */
	findUserCredentials() {
		logger.log('[findUserCredentials]');

		const queryParams = new URLSearchParams(window.location.search);

		if (queryParams.has('token')) {
			return `Bearer ${queryParams.get('token')}`;
		}

		if (queryParams.has('stbId')) {
			return queryParams.get('stbId');
		}

		return null;
	}

	/**
	 * Listener for API errors. Check token expiration and re-authorize if it's expired.
	 */
	onApiError(error) {
		logger.error('[onApiError]', error);

		const identifier = this.getIdentifier();
		if (!REGEX_UUID.test(identifier)) {
			try {
				const payload = JWT.decode(identifier);
				const expireTime = payload.exp;
				const now = Date.now().valueOf() / 1000;
				const isExpired = expireTime < now;
				logger.debug({ now, expireTime, isExpired });

				if (isExpired) {
					logger.warn('[Token timed out][Reauthorizing]');
					this.initialize();
				}
			} catch (err) {
				logger.error('[identifier is not a JWT]', err);
			}
		}
	}
}

export default new AppAuthManager();
